import styles from './GrzPage.module.scss'
import type { MouseEvent } from "react";
import {useCallback, useEffect, useRef, useState} from "react";
import {NavLink, useNavigate, useParams} from "react-router-dom";
import Title from "../ui/title/Title";
import {Container, Row} from "../ui/grid";
import PersonalData from "../personal-data/PersonalData";
import {GrzHelper} from "../../helper/GrzHelper";
import {getParkingSession, PaymentType} from 'api/query/ParkingSession';
import { prefetchActiveGrz } from "../../api/query/ActiveGrz";
import ApiCommon from "api/ApiCommon";
import Layout from '../layout'
import CheckSvg from "../../svg/CheckSvg";
import PaymentAutoSvg from "../../svg/PaymentAutoSvg";
import Triangle2Svg from "../../svg/Triangle2Svg";
import ForeinSvg from "../../svg/ForeinSvg";
import RussianFlagSvg from "../../svg/RussianFlagSvg";
import IcInfoSvg from "../../svg/IcInfoSvg";
import { InfoType } from "../../api/query/Info";

const GrzPage = () => {
  const navigate = useNavigate();
  const { zone } = useParams();

  const [openCountryState, setOpenCountryState] = useState(false);
  const [rusFormatState, setRusFormatState] = useState(true);
  const [checkedState, setCheckedState] = useState(false);
  const [openPersonalDataState, setOpenPersonalDataState] = useState(false);
  const [validGrzState, setValidGrzState] = useState(false);
  const [errorState, setErrorState] = useState(false);
  const [grzState, setGrzState] = useState('');
  const [rawGrzState, setRawGrzState] = useState('');
  const [grzStateWithoutSpace, setGrzStateWithoutSpace] = useState('');
  const [highlightConfirmState, setHighlightConfirmState] = useState(false);
  const [submittingState, setSubmittingState] = useState(false);
  const [submitStageState, setSubmitStageState] = useState(0);
  const [activeGrzState, setActiveGrzState] = useState<string[]>([]);
  const [recentGrzState, setRecentGrzState] = useState<string[]>([]);
  const [suggestionsOpenState, setSuggestionsOpenState] = useState(false);
  const countryButtonRef = useRef<HTMLButtonElement>(null);
  const countryOptionRef = useRef<HTMLButtonElement>(null);
  const grzInputRef = useRef<HTMLInputElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);
  const lastInputLogRef = useRef('');
  const submitTimersRef = useRef<number[]>([]);
  const formatTouchedRef = useRef(false);
  const grzInputTouchedRef = useRef(false);

  const normalizeGrz = (value: string) => value.replace(/\s/g, '').toUpperCase();
  const recentGrzStorageKey = `parking-pay:recent-grz:${zone ?? ''}`;
  const saveRecentGrz = (value: string) => {
    const normalized = normalizeGrz(value);
    if (!normalized) return;
    const nextRecent = [normalized, ...recentGrzState.filter((item) => normalizeGrz(item) !== normalized)].slice(0, 5);
    setRecentGrzState(nextRecent);
    try {
      window.localStorage.setItem(recentGrzStorageKey, JSON.stringify(nextRecent));
    } catch {
      // Recent suggestions are optional.
    }
  };
  const formatRusGrz = (value: string) => {
    const grzWithoutSpaces = GrzHelper.clearRus(value);
    const grzArr = grzWithoutSpaces.match(/^([\s\S])([\s\S]{0,3})([\s\S]{0,2})([\s\S]{0,3})/);
    return (Array.isArray(grzArr) ? grzArr : []).filter((v, i) => (i > 0 && v)).join(' ');
  };
  const formatSuggestionGrz = (value: string) => rusFormatState ? formatRusGrz(value) : normalizeGrz(value);
  const getFormattedMatchRange = (formattedValue: string, normalizedQuery: string) => {
    if (!normalizedQuery) return null;
    const normalizedValue = formattedValue.replace(/\s/g, '');
    const matchStart = normalizedValue.indexOf(normalizedQuery);
    if (matchStart < 0) return null;

    let normalizedIndex = 0;
    let start = -1;
    let end = -1;

    for (let i = 0; i < formattedValue.length; i++) {
      if (formattedValue[i] === ' ') continue;
      if (normalizedIndex === matchStart) start = i;
      normalizedIndex += 1;
      if (start >= 0 && normalizedIndex === matchStart + normalizedQuery.length) {
        end = i + 1;
        break;
      }
    }

    return start >= 0 && end >= 0 ? { start, end } : null;
  };

  const submitGrz = (value: string) => {
    if (submittingState) return;
    saveRecentGrz(value);

    submitTimersRef.current.forEach((timerId) => window.clearTimeout(timerId));
    submitTimersRef.current = [
      window.setTimeout(() => setSubmitStageState(1), 10000),
      window.setTimeout(() => setSubmitStageState(2), 25000),
    ];
    setSubmitStageState(0);
    setSubmittingState(true);
    getParkingSession(zone, PaymentType.grz, value)
      .then((data) => {
        if ('errorMessage' in data) {
          navigate(`/${zone}/grz/${value}/error`);
          return;
        }

        const sessions = data.result || [];
        if (sessions.length === 1 && sessions[0].sessionID) {
          navigate(`/${zone}/grz/${value}/confirmation?sessionID=${sessions[0].sessionID}`);
        } else {
          navigate(`/${zone}/grz/${value}`);
        }
      })
      .finally(() => {
        submitTimersRef.current.forEach((timerId) => window.clearTimeout(timerId));
        submitTimersRef.current = [];
        setSubmitStageState(0);
        setSubmittingState(false);
      });
  };

  const activeGrzSuggestions = activeGrzState
    .filter((item) => {
      const normalizedInput = normalizeGrz(grzStateWithoutSpace);
      if (normalizedInput.length < 2) return false;
      return normalizeGrz(item).includes(normalizedInput);
    })
    .sort((a, b) => {
      const aRecentIndex = recentGrzState.findIndex((item) => normalizeGrz(item) === normalizeGrz(a));
      const bRecentIndex = recentGrzState.findIndex((item) => normalizeGrz(item) === normalizeGrz(b));
      if (aRecentIndex >= 0 && bRecentIndex >= 0) return aRecentIndex - bRecentIndex;
      if (aRecentIndex >= 0) return -1;
      if (bRecentIndex >= 0) return 1;
      return 0;
    })
    .slice(0, 20);
  const normalizedSuggestionQuery = normalizeGrz(grzStateWithoutSpace);

  const handleChangeGrz2 = (e: any) => {
    grzInputTouchedRef.current = true;
    const grzValue = e.target.value;
    setRawGrzState(grzValue);
    if (rusFormatState) {
      setGrzState(formatRusGrz(grzValue));
    } else {
      setGrzState(grzValue.toUpperCase());
    }
    setOpenCountryState(false);
    setSuggestionsOpenState(true);
  }

  useEffect(() => {
    prefetchActiveGrz(zone).then((items) => {
      setActiveGrzState(items);
    });
  }, [zone]);

  useEffect(() => {
    try {
      const stored = window.localStorage.getItem(recentGrzStorageKey);
      const parsed = stored ? JSON.parse(stored) : [];
      setRecentGrzState(Array.isArray(parsed) ? parsed.filter((item): item is string => typeof item === 'string').slice(0, 5) : []);
    } catch {
      setRecentGrzState([]);
    }
  }, [recentGrzStorageKey]);

  useEffect(() => {
    setGrzStateWithoutSpace(grzState.replace(/\s/g, ''));
    const validGrz = rusFormatState ? GrzHelper.testRus(grzState): GrzHelper.testForeign(grzState);
    setErrorState(!!grzState && !validGrz);
    setValidGrzState(validGrz);
  }, [grzState, openCountryState, rusFormatState]);

  useEffect(() => {
    if (zone !== '665' || !rawGrzState) return;

    const rawWithoutSpaces = rawGrzState.replace(/\s/g, '');
    if (rawWithoutSpaces.length < 2) return;

    const cleaned = GrzHelper.clearRus(rawGrzState);
    const logKey = `${rusFormatState}|${rawGrzState}|${grzState}|${cleaned}|${validGrzState}|${errorState}`;
    if (lastInputLogRef.current === logKey) return;

    const timeoutId = window.setTimeout(() => {
      lastInputLogRef.current = logKey;
      ApiCommon.logDiagnostic('/grz-input', zone, {
        reason: 'grz_input_trace',
        rusFormat: rusFormatState,
        valid: validGrzState,
        error: errorState,
        canNext: validGrzState && !errorState,
        rawInput: rawGrzState,
        cleanedInput: cleaned,
        formattedInput: grzState,
        valueForRequest: grzStateWithoutSpace,
        wasChangedByClear: rawGrzState !== cleaned,
        wasChangedByFormat: cleaned !== grzState,
        rawLength: rawWithoutSpaces.length,
        cleanedLength: cleaned.length,
        formattedLength: grzStateWithoutSpace.length,
        rawCodePoints: Array.from(rawGrzState).map((char) => char.codePointAt(0)?.toString(16).toUpperCase()),
        cleanedCodePoints: Array.from(cleaned).map((char) => char.codePointAt(0)?.toString(16).toUpperCase()),
        formattedCodePoints: Array.from(grzState).map((char) => char.codePointAt(0)?.toString(16).toUpperCase()),
      });
    }, 800);

    return () => window.clearTimeout(timeoutId);
  }, [zone, rawGrzState, grzState, grzStateWithoutSpace, rusFormatState, validGrzState, errorState]);

  useEffect(() => {
    if (!openCountryState && !suggestionsOpenState) return;

    const handleDocumentPointerDown = (event: PointerEvent) => {
      const target = event.target as Node;
      if (grzInputRef.current?.contains(target)) return;
      if (countryButtonRef.current?.contains(target)) return;
      if (countryOptionRef.current?.contains(target)) return;
      if (suggestionsRef.current?.contains(target)) return;
      setOpenCountryState(false);
      setSuggestionsOpenState(false);
    };

    document.addEventListener('pointerdown', handleDocumentPointerDown, true);
    return () => document.removeEventListener('pointerdown', handleDocumentPointerDown, true);
  }, [openCountryState, suggestionsOpenState]);

  const grzChangerMode = () => {
    formatTouchedRef.current = true;
    setRusFormatState((s) => (!s));
    setOpenCountryState(!openCountryState);
    setSuggestionsOpenState(false);
    setGrzState('');
    setErrorState(false)
  }

  const handleInfoLoaded = useCallback((info: InfoType) => {
    if (formatTouchedRef.current || grzInputTouchedRef.current) return;
    setRusFormatState(!info.result.internationalPlateFormat);
  }, []);

  const canNext = () => {
    //return (rusFormatState && validGrzState && !errorState) || (!rusFormatState && !errorState);
    return (validGrzState && !errorState);
  }

  const highlightConfirmButton = () => {
    setHighlightConfirmState(false);
    window.setTimeout(() => {
      setHighlightConfirmState(true);
      window.setTimeout(() => setHighlightConfirmState(false), 700);
    }, 0);
  };

  const handleConfirmClick = (event: MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();

    if (!canNext() || submittingState) return;

    submitGrz(grzStateWithoutSpace);
  };

  const submitText = submittingState
    ? ['Проверяем...', 'Ищем дольше обычного...', 'Пожалуйста, подождите...'][submitStageState]
    : 'Подтвердить';

  return (
    <Layout infoListener={handleInfoLoaded}>
      <section className={`${styles.section} ${styles.fixHeight}`}>
        <Container>
          <div className={styles.img}>
            <PaymentAutoSvg />
          </div>
          <div className={styles.form}>

            <Title text="Введите номер автомобиля" />

            <div className={styles.form_input}>
              <Row center>
                <button ref={countryButtonRef} disabled={submittingState} onClick={() => {
                  setSuggestionsOpenState(false);
                  setOpenCountryState(!openCountryState)
                }} className={`${styles.form_input_btn} ${openCountryState ? styles.form_input_btn_active : ''} ${errorState ? styles.btn_w_error : ''}`}>
                  <Row center>
                    {rusFormatState ? <RussianFlagSvg /> : <ForeinSvg />}
                    <Triangle2Svg />
                  </Row>
                </button>
                <input
                  ref={grzInputRef}
                  className={errorState ? styles.input_error : ''}
                  value={grzState}
                  onChange={handleChangeGrz2}
                  onFocus={() => {
                    if (activeGrzSuggestions.length > 0) {
                      setOpenCountryState(false);
                      setSuggestionsOpenState(true);
                    }
                  }}
                  disabled={submittingState}
                  type="text"
                  placeholder={rusFormatState ? 'X 000 XX 00' : 'XXXXXXXX'}>
                </input>
              </Row>

              <button
                ref={countryOptionRef}
                disabled={submittingState}
                onClick={grzChangerMode}
                className={`${styles.form_input_btn_country} ${errorState ? styles.country_btn_w_error : ''} ${openCountryState ? styles.form_input_btn_country_active : ''} `}
              >
                {!rusFormatState ? <RussianFlagSvg /> : <ForeinSvg />}
              </button>

              {suggestionsOpenState && activeGrzSuggestions.length > 0 ? (
                <div className={styles.suggestions} ref={suggestionsRef}>
                  {activeGrzSuggestions.map((item) => {
                    const formatted = formatSuggestionGrz(item);
                    const matchRange = getFormattedMatchRange(formatted, normalizedSuggestionQuery);
                    const isRecent = recentGrzState.some((recentItem) => normalizeGrz(recentItem) === normalizeGrz(item));
                    return (
                      <button
                        type="button"
                        key={item}
                        className={styles.suggestion_item}
                        onClick={() => {
                          const normalized = normalizeGrz(item);
                          setGrzState(formatSuggestionGrz(normalized));
                          setGrzStateWithoutSpace(normalized);
                          setSuggestionsOpenState(false);
                          submitGrz(normalized);
                        }}
                      >
                        <span>
                          {matchRange ? (
                            <>
                              {formatted.substring(0, matchRange.start)}
                              <span className={styles.suggestion_match}>{formatted.substring(matchRange.start, matchRange.end)}</span>
                              {formatted.substring(matchRange.end)}
                            </>
                          ) : formatted}
                        </span>
                        {isRecent ? <span className={styles.suggestion_recent}>Недавно выбирали</span> : null}
                      </button>
                    );
                  })}
                </div>
              ) : null}

            </div>

            <div className={`${styles.error} ${errorState ? '' : styles.hidden}`}>Некорректный формат номера автомобиля</div>

            <div className={`${styles.form_checkbox} ${styles.hidden}`}>
              <label>
                <input type="checkbox" onChange={() => setCheckedState((s) => (!s))} />
                <Row>
                  <div
                    className={`${styles.checkbox} ${checkedState ? styles.checkbox__active : ""} `}
                    aria-hidden="true"
                  />
                  <div className={styles.form_checkbox__text}>
                    Принимаю условия
                    <button
                      onClick={() => { setOpenPersonalDataState((s) => (!s)) }}
                      className={styles.form_checkbox__personal_data}
                    >
                      пользовательского соглашения
                    </button>
                  </div>
                </Row>
              </label>
            </div>

            <NavLink
              onClick={handleConfirmClick}
              to=""
              aria-disabled={!canNext() || submittingState}
              className={`${styles.form_btn} ${canNext() ? styles.form_btn__active : ''} ${submittingState ? styles.form_btn_loading : ''} ${highlightConfirmState ? styles.form_btn_highlight : ''}`}
            >
              {submittingState ? <span className={styles.form_btn_spinner} /> : <CheckSvg />}
              <span key={submitText} className={submittingState ? styles.form_btn_text_loading : ''}>{submitText}</span>
            </NavLink>

            <div className={styles.info_note} onClick={highlightConfirmButton}>
              <IcInfoSvg />
              <span>Убедитесь, что госномер чистый и читаемый, затем нажмите <span className={styles.info_note_accent}>ПОДТВЕРДИТЬ</span></span>
            </div>

          </div>
        </Container>
        <PersonalData visible={openPersonalDataState} />
      </section>
    </Layout>
  )
}

export default GrzPage;
